/**
 * Docked panel for the selected own city: hp, fire and pooled stock, then
 * Train (units, stores, deploy, soldiers waiting outside), Build (placement
 * cards), Workers (targets, slots, work focus; CityWorkersTab) and Upgrades
 * (this city's level, the levels of the buildings in its territory, the unit
 * upgrades and rank-ups usable here, all as the shared `UpgradeCard`) tabs.
 * The Units panel still upgrades and ranks up per type, as many as the stock
 * covers. Fixed size, so nothing moves as cards enable or reasons appear;
 * every refusal comes from the sim's checks.
 */
import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { HexTileMap } from '@/game/hexmap';
import type { RuleSet } from '@/game/rules/schema';
import { unitGlyph } from '@/game/rules/ui';
import {
  type Building,
  type BuildingType,
  buildingCountIn,
  buildingLevel,
  buildingMaxHp,
  buildingRange,
  buildingStrength,
  type Command,
  canSpawn,
  capCount,
  cityBonuses,
  cityStores,
  cityWorkerCap,
  fromMilli,
  levelTitle,
  maxLevel,
  type PlayerId,
  type SimState,
  storageCapacity,
  territoryOwners,
  unitAmounts,
  unitCap,
  unitCost,
} from '@/game/sim';
import { stockLabel } from '@/game/view/cities';
import { cityWorkers } from '@/game/view/cityWorkers';
import { labelOrDefault, type Names } from '@/game/view/names';
import { unitCountText, unitsText } from '@/game/view/promotions';
import {
  barracksFullText,
  buildingCityLevel,
  cityAtLevelText,
  costLabel,
  missingLabel,
  overCapText,
  placementHint,
  spawnRejectionText,
  unitCityLevel,
} from '@/game/view/reasons';
import {
  orderedResources,
  RESOURCES_SHARED_TEXT,
  resourceIcon,
  stockInUnits,
} from '@/game/view/resources';
import { tickRoster, tickWorkBoard } from '@/game/view/tickCache';
import { cityQueues, queuedCounts, type StoreQueue, trainTimeText } from '@/game/view/training';
import type { UpgradePreview } from '@/game/view/upgradePreview';
import { upgradeProgress } from '@/game/view/upgradeProgress';
import { cityUpgrades, type UpgradeEntry } from '@/game/view/upgradesList';
import { CityWorkersTab } from './CityWorkersTab';
import { Cost } from './Cost';
import { DockPanel } from './hud/DockPanel';
import { usePersisted } from './persisted';
import { UnitCard } from './UnitCard';
import { UpgradeActionRow, UpgradeCard } from './UpgradeCard';
import { UpgradeProgress } from './UpgradeProgress';
import { EntryAction, UpgradeFold, useTickMemo } from './UpgradeRows';
import { VerbLabel } from './VerbLabel';
import { waitingOutside } from './waitingOutside';

function canAfford(stockpile: Record<string, number>, cost: Record<string, number>): boolean {
  for (const [res, amount] of Object.entries(cost)) {
    if ((stockpile[res] ?? 0) < amount) return false;
  }
  return true;
}

export interface LevelSectionProps {
  state: SimState;
  building: Building;
  playerId: PlayerId;
  rules: RuleSet;
  terrain: HexTileMap;
  dispatch: (c: Command) => void;
  /** Stable city numbers (`numberCities`), to name the city an era refusal waits on. */
  cityNumbers?: ReadonlyMap<string, number>;
  /** The player's own names (client-local), for the same. */
  names?: Names;
  /** Point at own units (the card's Affects): the match rings them and the Units list marks them. */
  onHighlight?: (ids: readonly string[]) => void;
}

/**
 * Header chip for a building with levels: "Level 2/3", or "Max level" once
 * there; a titled level (a city's era, "Mechanised") reads "Industrial 5/7".
 */
export function LevelChip({ rules, building }: { rules: RuleSet; building: Building }) {
  const def = rules.buildings[building.type];
  if (!def?.levels) return null;
  const level = buildingLevel(building);
  const max = maxLevel(def);
  const title = levelTitle(def, level);
  const titled = title !== def.name;
  return (
    <span
      title={`Level ${level}/${max}${titled ? ` (${title})` : ''}`}
      className={`chip shrink-0 tabular-nums ${level >= max ? 'text-ok' : ''}`}
    >
      {titled ? `${title} ${level}/${max}` : level >= max ? 'Max level' : `Level ${level}/${max}`}
    </span>
  );
}

/**
 * The next level of a city or building as an `UpgradeCard` (body; its action
 * goes in the panel's pinned footer via `LevelAction`). `preview` is null at
 * max level.
 */
export function LevelCard({
  preview,
  onHighlight,
}: {
  preview: UpgradePreview | null;
  onHighlight?: (ids: readonly string[]) => void;
}) {
  if (!preview) return <p className="px-1 text-fg-subtle">Max level</p>;
  return <UpgradeCard preview={preview} externalAction onHighlight={onHighlight} />;
}

/** The Level up button (and why it's disabled), for a panel's footer slot. */
export function LevelAction({
  preview,
  state,
  rules,
  building,
  playerId,
  dispatch,
}: {
  preview: UpgradePreview | null;
  state: SimState;
  rules: RuleSet;
  building: Building;
  playerId: PlayerId;
  dispatch: (c: Command) => void;
}) {
  if (!preview) return null;
  const timer = upgradeProgress(state, rules, building, playerId);
  return (
    <UpgradeActionRow
      preview={preview}
      stock={stockInUnits(state, playerId)}
      onConfirm={() => dispatch({ type: 'upgrade', buildingId: building.id })}
      phase={timer ? 'inProgress' : 'available'}
      progress={
        timer && (
          <UpgradeProgress
            progress={timer}
            onCancel={() => dispatch({ type: 'cancelUpgrade', buildingId: building.id })}
          />
        )
      }
    />
  );
}

const WORK_FOCUS_TEXT =
  'Workers from this city prefer this resource (up to ~60% of its gatherers) but keep a mix';

/**
 * The resources a city's worker focus may name: ones with wild sources, and
 * ones only buildings give (metal: mines) once the player has such a building
 * or this city's level opens one. The current focus always shows.
 */
function focusResources(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  city: Building
): string[] {
  const level = buildingLevel(city);
  const amounts = Object.fromEntries(Object.keys(rules.resources).map((r) => [r, 1]));
  return orderedResources(amounts).filter((res) => {
    if (res === city.workFocus) return true;
    if ((rules.resources[res]?.gatheredFrom ?? []).length > 0) return true;
    return Object.entries(rules.buildings).some(
      ([type, def]) =>
        def.provides?.resource === res &&
        ((def.placement?.cityLevel ?? 1) <= level ||
          Object.values(state.buildings).some((b) => b.owner === playerId && b.type === type))
    );
  });
}

interface CityPanelProps {
  state: SimState;
  /**
   * The real state (`state` is the viewer's, viewerState.ts): unit caps and
   * spawn checks count only units really theirs, so a rival spy disguised as
   * one of their workers takes no slot. Defaults to `state`.
   */
  realState?: SimState;
  city: Building;
  /** Its stable number (`numberCities`). */
  number: number;
  /** Every city's number, to name the city a level refusal waits on. */
  cityNumbers?: ReadonlyMap<string, number>;
  /** The player's own names (client-local); a named city shows its name. */
  names?: Names;
  onHighlight?: (ids: readonly string[]) => void;
  playerId: PlayerId;
  rules: RuleSet;
  terrain: HexTileMap;
  dispatch: (c: Command) => void;
  /** Place a site of `type` in this city's territory. */
  onPlace: (type: BuildingType, cityId: string) => void;
  /** Select units (ones stored in this city's barracks). */
  onSelectUnits: (ids: string[]) => void;
  /** The selected units, so Select idle cycles. */
  selectedUnitIds: readonly string[];
  /** Move the camera to the city. */
  onPan: () => void;
  onClose: () => void;
}

/**
 * A training building's queue on its line of the city panel, as one
 * fixed-width chip (so the line never re-wraps while units train): the unit
 * closest to done with its seconds left, how many train and wait ("2+3"),
 * and a progress bar per unit training. Click cancels the last one queued
 * (full refund); the title lists them all. It sits right after its
 * building's label, before the stored-unit chips, so units coming out of
 * training never move it under a cancel click.
 */
function TrainingQueue({
  rules,
  queue,
  onCancel,
}: {
  rules: RuleSet;
  queue: StoreQueue | undefined;
  onCancel: (trainingId: string) => void;
}) {
  if (!queue || queue.active.length + queue.waiting.length === 0) return null;
  const next = queue.active.reduce<StoreQueue['active'][number] | undefined>(
    (best, s) => (!best || s.secondsLeft < best.secondsLeft ? s : best),
    undefined
  );
  const newest =
    queue.waiting[queue.waiting.length - 1] ?? queue.active[queue.active.length - 1]?.entry;
  const nameOf = (type: string) => rules.units[type]?.name ?? type;
  const lines = [
    ...queue.active.map(
      ({ entry, secondsLeft }) =>
        `${nameOf(entry.unitType)}: ${secondsLeft > 0 ? `${secondsLeft}s left` : 'done, waiting for room to come out'}`
    ),
    ...(queue.waiting.length > 0
      ? [
          `Waiting for a slot: ${unitsText(
            rules,
            queue.waiting.map((t) => t.unitType)
          )}`,
        ]
      : []),
  ];
  return (
    <button
      type="button"
      title={`Training:\n${lines.join('\n')}\nClick to cancel the last one queued (${nameOf(newest.unitType)}, full refund: ${costLabel(unitAmounts(newest.paid))})`}
      onClick={() => onCancel(newest.id)}
      className="chip relative w-[6.5rem] shrink-0 justify-between overflow-hidden pb-1 tabular-nums transition hover:bg-fill-hover"
    >
      <span className="whitespace-nowrap">
        {next ? `${unitGlyph(rules, next.entry.unitType)} ${next.secondsLeft}s` : 'waiting'}
      </span>
      <span className="text-fg-muted">
        {queue.active.length}
        {queue.waiting.length > 0 ? `+${queue.waiting.length}` : ''}
      </span>
      {queue.active.length > 0 && (
        <span className="absolute inset-x-1.5 bottom-0.5 flex h-0.5 gap-0.5">
          {queue.active.map(({ entry, progress }) => (
            <span key={entry.id} className="h-full flex-1 overflow-hidden rounded-full bg-fill">
              <span
                className="block h-full rounded-full bg-accent"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </span>
          ))}
        </span>
      )}
    </button>
  );
}

const CITY_TABS = ['train', 'build', 'workers', 'upgrades'] as const;
type CityTab = (typeof CITY_TABS)[number];
const TAB_LABELS: Record<CityTab, ReactNode> = {
  train: <VerbLabel verb="train" />,
  build: 'Build',
  workers: 'Workers',
  upgrades: 'Upgrades',
};

/** Selected city: hp and fire rate, then tabs for Train (spawns here), Build (sites in its territory) and Upgrades (levels, unit upgrades, rank-ups). */
export function CityPanel({
  state,
  realState = state,
  city,
  number,
  cityNumbers,
  names,
  onHighlight,
  playerId,
  rules,
  terrain,
  dispatch,
  onPlace,
  onSelectUnits,
  selectedUnitIds,
  onPan,
  onClose,
}: CityPanelProps) {
  const [savedTab, setTab] = usePersisted<string>('cityTab', 'train');
  // The Train card being hovered or focused, and where the dock is, for its stats popover.
  const [hover, setHover] = useState<{
    cityId: string;
    type: string;
    bottom: number;
    left: number;
  } | null>(null);
  const tab: CityTab = (CITY_TABS as readonly string[]).includes(savedTab)
    ? (savedTab as CityTab)
    : 'train';
  const def = rules.buildings[city.type];
  const stockpile = stockInUnits(state, playerId);
  // This city's upgrades: live on their tab, else a rough count for its label.
  const upgrades = useTickMemo(
    () =>
      cityUpgrades(
        state,
        { terrain, rules },
        playerId,
        city.id,
        realState,
        cityNumbers ?? new Map(),
        names
      ),
    state.tick,
    tab === 'upgrades' ? 1 : 8,
    `${city.id}:${tab}`
  );
  const upgradeEntries = [
    ...(upgrades.city ? [upgrades.city] : []),
    ...upgrades.buildings,
    ...upgrades.units,
  ];
  const readyUpgrades = upgradeEntries.filter((e) => e.canDo).length;
  // The open card (the city's own by default; '' = all folded).
  const [openId, setOpenId] = useState<string | null>(null);
  const open = openId ?? upgrades.city?.id ?? '';
  const toggleCard = (id: string) => setOpenId(open === id ? '' : id);
  // Locked cards name this city: unlocks (eras, barracks) are per city.
  const cityLabel = labelOrDefault(names, city.id, `${def?.name ?? 'City'} ${number}`);
  // The next era's units and buildings show, locked; later eras' stay hidden.
  const cityLevel = buildingLevel(city);
  const spawnUnits = (def?.spawns ?? []).filter((u) => unitCityLevel(rules, u) <= cityLevel + 1);
  const dense = spawnUnits.length > 15;
  const placeableTypes = Object.keys(rules.buildings).filter(
    (t) => rules.buildings[t]?.placement && buildingCityLevel(rules, t) <= cityLevel + 1
  );
  const owners = territoryOwners(state, rules);
  const bonuses = cityBonuses(state, rules, owners);
  const strength = buildingStrength(rules, city, bonuses);
  const range = buildingRange(rules, city);
  // This city's barracks and the units stored in them.
  const stores = cityStores(state, rules, city.id, owners).map((b) => ({
    building: b,
    capacity: storageCapacity(rules, b),
    units: Object.values(state.units).filter((u) => u.inside === b.id),
  }));
  // Units training here (timed units), per training building, and per type for the cards.
  const queues = cityQueues(state, rules, city, owners);
  // Queues at a building no longer this city's store (a newer city took its
  // tile): shown on their own so they stay visible and cancellable.
  const storeIds = new Set(stores.map((s) => s.building.id));
  const strayQueues = [...queues].filter(([id]) => !storeIds.has(id));
  const queued = queuedCounts(city);
  const cancelTraining = (trainingId: string) =>
    dispatch({ type: 'cancelTraining', cityId: city.id, trainingId });
  // Soldiers trained here idling outside its full barracks.
  const waiting = waitingOutside(state, rules, playerId).get(city.id) ?? [];
  // Placed buildings already in this city's territory, for maxPerCity.
  const builtHere = (type: BuildingType) => buildingCountIn(state, owners, playerId, type, city.id);

  // Where the dock sits, so the popover stands just above it.
  const showPreview = (card: HTMLElement, type: string) => {
    const dock = card.closest('.hud-dock')?.getBoundingClientRect();
    if (!dock) return;
    setHover({
      cityId: city.id,
      type,
      bottom: window.innerHeight - dock.top + 8,
      left: dock.left + dock.width / 2,
    });
  };
  const preview = tab === 'train' && hover?.cityId === city.id ? hover : null;
  const focusList = focusResources(state, rules, playerId, city);
  // Once per tick, not per render: the board builds a fresh automation cache.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the tick and city, not the snapshot's identity
  const workers = useMemo(
    () =>
      tab === 'workers'
        ? cityWorkers({
            state,
            ctx: { terrain, rules },
            city,
            owners,
            ...tickWorkBoard(state, { terrain, rules }, city.id),
            idleIds: tickRoster(state, { terrain, rules, playerId }).idle,
            resources: focusList,
            cityNumbers: cityNumbers ?? new Map([[city.id, number]]),
            cap: cityWorkerCap(rules, city),
          })
        : null,
    [tab, state.tick, city.id]
  );

  // Worker focus: what this city's auto workers favour (setWorkFocus). Scrolls
  // sideways rather than clipping a chip on a narrow dock.
  const focusControl = (
    <fieldset
      aria-label="Worker focus"
      className="flex min-w-0 items-center gap-1 overflow-x-auto"
      title={WORK_FOCUS_TEXT}
    >
      {['balanced', ...focusList].map((focus) => {
        const selected = (city.workFocus ?? 'balanced') === focus;
        const name = focus === 'balanced' ? 'Balanced' : (rules.resources[focus]?.name ?? focus);
        return (
          <button
            key={focus}
            type="button"
            aria-pressed={selected}
            title={
              focus === 'balanced'
                ? 'Workers from this city spread over every resource by what each pays'
                : WORK_FOCUS_TEXT
            }
            onClick={() => {
              if (!selected) dispatch({ type: 'setWorkFocus', cityId: city.id, focus });
            }}
            className={`chip shrink-0 whitespace-nowrap py-0 text-[11px] transition ${selected ? 'chip-selected' : 'hover:bg-fill-hover'}`}
          >
            {focus === 'balanced' ? name : `${resourceIcon(focus)} ${name}`}
          </button>
        );
      })}
    </fieldset>
  );

  const openEntry = upgradeEntries.find((e) => e.id === open);
  // An entry with buildings already upgrading shows the soonest one's timer (and Cancel)
  // above its button, or in place of it when nothing else of the entry can go.
  const timers = (openEntry?.upgrading ?? [])
    .map((id) => {
      const b = state.buildings[id];
      const progress = b && upgradeProgress(state, rules, b, playerId);
      return progress ? { id, progress } : null;
    })
    .filter((t) => t !== null)
    .sort((a, b) => a.progress.secondsLeft - b.progress.secondsLeft);
  const entryTimer = timers[0];
  const group = { open, stock: stockpile, onToggle: toggleCard, onHighlight };

  return (
    <DockPanel
      // Wider than the other docks, so all fourteen Train cards fit in three
      // rows of five with full names and costs.
      size="wide"
      kind="city"
      title={cityLabel}
      level={<LevelChip rules={rules} building={city} />}
      chips={
        <>
          <span className="chip tabular-nums">
            {Math.ceil(fromMilli(city.hp))}/
            {Math.ceil(fromMilli(buildingMaxHp(state, rules, city, bonuses)))} hp
          </span>
          {strength > 0 && (
            <span className="chip tabular-nums">
              fires {strength}/s{range > 1 ? ` · ${range} tiles` : ''}
            </span>
          )}
          {city.lastStand && (
            <span
              className="chip shrink-0 text-warn"
              title={`Your only city: +${Math.round(rules.pacing.lastStandHp * 100)}% hp and ×${rules.pacing.lastStandFire} fire`}
            >
              last stand
            </span>
          )}
        </>
      }
      onLocate={onPan}
      onClose={onClose}
      toolbar={
        <>
          {/* Resources are one pool, spent from any city: the line shows the total
              (what every card here is judged against); what this city holds, which
              only matters if it falls, is in the tooltip. Truncates with an
              ellipsis (full numbers in the tooltip). */}
          <div className="flex min-w-0 shrink-0 items-center gap-2 overflow-hidden text-[11px]">
            <span
              className="flex min-w-0 shrink items-center gap-1 overflow-hidden px-1 tabular-nums text-fg-muted"
              title={`${RESOURCES_SHARED_TEXT}.\nTotal: ${stockLabel(stockpile)}\nHeld in ${cityLabel}: ${stockLabel(unitAmounts(city.stock ?? {}))}`}
            >
              <span className="shrink-0 text-fg-subtle">Total</span>
              <span className="truncate">{stockLabel(stockpile)}</span>
            </span>
          </div>
        </>
      }
      tabs={
        <div role="tablist" className="flex shrink-0 gap-1 rounded-full bg-fill p-0.5">
          {CITY_TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={t === tab}
              onClick={() => {
                setTab(t);
                setHover(null);
              }}
              className={`chip flex-1 justify-center py-0.5 text-xs transition ${t === tab ? 'chip-selected' : 'bg-transparent text-fg-muted hover:bg-fill-hover'}`}
            >
              {TAB_LABELS[t]}
              {t === 'upgrades' && readyUpgrades > 0 && (
                <span className="tabular-nums text-accent">{readyUpgrades}</span>
              )}
            </button>
          ))}
        </div>
      }
      footer={
        <>
          {/* Storage (barracks, factory), pinned under the Train cards on one
              fixed-height line, so it never takes rows from the cards: each
              store's count, its training queue (fixed width, before anything
              that changes as units come out) and its stored units by type (click
              selects them) scroll sideways in the part on the left; soldiers
              waiting outside a full store (click selects them), one Deploy all
              for every store and the Units panel hint (unit upgrades and
              rank-ups) are pinned on the right, so a full barracks' chips never
              push Deploy all out of view. */}
          {tab === 'train' && (
            <div className="flex h-6 shrink-0 items-center gap-1 overflow-hidden px-1 text-[11px]">
              <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto overflow-y-hidden">
                {stores.map(({ building, capacity, units }, i) => {
                  const byType = new Map<string, string[]>();
                  for (const u of units) byType.set(u.type, [...(byType.get(u.type) ?? []), u.id]);
                  return (
                    <Fragment key={building.id}>
                      {i > 0 && <span className="shrink-0 text-fg-subtle">·</span>}
                      <span className="shrink-0 whitespace-nowrap text-fg-muted">
                        {rules.buildings[building.type]?.name ?? building.type}{' '}
                        <span
                          className={`tabular-nums ${units.length >= capacity ? 'text-warn' : 'text-fg-subtle'}`}
                        >
                          {units.length}/{capacity}
                        </span>
                      </span>
                      <TrainingQueue
                        rules={rules}
                        queue={queues.get(building.id)}
                        onCancel={cancelTraining}
                      />
                      {[...byType].map(([type, ids]) => (
                        <button
                          key={type}
                          type="button"
                          title="Select them; a move order brings them out"
                          onClick={() => onSelectUnits(ids)}
                          className="chip shrink-0 tabular-nums transition hover:bg-fill-hover"
                        >
                          {unitCountText(rules, type, ids.length)}
                        </button>
                      ))}
                    </Fragment>
                  );
                })}
                {strayQueues.map(([id, queue], i) => (
                  <Fragment key={id}>
                    {(stores.length > 0 || i > 0) && (
                      <span className="shrink-0 text-fg-subtle">·</span>
                    )}
                    <span className="shrink-0 text-fg-muted">
                      {rules.buildings[state.buildings[id]?.type ?? '']?.name ?? 'Barracks'}
                    </span>
                    <TrainingQueue rules={rules} queue={queue} onCancel={cancelTraining} />
                  </Fragment>
                ))}
              </div>
              {waiting.length > 0 && (
                <button
                  type="button"
                  title="Trained here but the barracks is full: they wait outside. Once a slot frees, move them onto the city or barracks to go in. Click to select them"
                  onClick={() => onSelectUnits(waiting)}
                  className="chip shrink-0 tabular-nums text-warn transition hover:bg-fill-hover"
                >
                  {waiting.length} waiting outside
                </button>
              )}
              {stores.some((s) => s.units.length > 0) && (
                <button
                  type="button"
                  title="Bring every stored unit out onto free tiles around its building"
                  onClick={() =>
                    dispatch({
                      type: 'deploy',
                      unitIds: stores.flatMap((s) => s.units.map((u) => u.id)),
                    })
                  }
                  className="btn btn-secondary h-5 shrink-0 px-2 text-[11px]"
                >
                  Deploy all
                </button>
              )}
            </div>
          )}
          {tab === 'upgrades' &&
            openEntry &&
            (entryTimer ? (
              <>
                <UpgradeProgress
                  progress={entryTimer.progress}
                  onCancel={() => dispatch({ type: 'cancelUpgrade', buildingId: entryTimer.id })}
                />
                {openEntry.canDo && (
                  <EntryAction entry={openEntry} stock={stockpile} dispatch={dispatch} />
                )}
              </>
            ) : (
              <EntryAction entry={openEntry} stock={stockpile} dispatch={dispatch} />
            ))}
        </>
      }
    >
      <div role="tabpanel">
        {/* Stats of the hovered Train card. In a portal: the dock scrolls (and its
            backdrop filter would pin a fixed child), so it would be clipped. */}
        {preview &&
          createPortal(
            <div
              role="tooltip"
              className="panel pointer-events-none fixed z-30 w-80 -translate-x-1/2 overflow-hidden p-3"
              style={{
                bottom: preview.bottom,
                left: preview.left,
                maxHeight: `calc(100vh - ${preview.bottom}px - 0.75rem)`,
              }}
            >
              <UnitCard id="unit-preview" rules={rules} type={preview.type} />
            </div>,
            document.body
          )}
        {/* Train: units spawn at this city */}
        {tab === 'train' &&
          (spawnUnits.length === 0 ? (
            <p className="px-1 text-fg-subtle">This city can't train units</p>
          ) : (
            // Five columns, at most three rows (this era's units and the next
            // one's): every name and cost in full; every card keeps a one-line
            // reason slot, so a reason appearing never resizes the grid, and
            // all of them fit without scrolling above the one pinned line of
            // stores. Past fifteen cards (the late eras, with
            // healers, medics and engineers) six columns keep it to three
            // rows, names and costs truncated (the title has them in full).
            <div className={`grid gap-1 ${dense ? 'grid-cols-6' : 'grid-cols-5'}`}>
              {spawnUnits.map((unitType) => {
                const unitDef = rules.units[unitType];
                if (!unitDef) return null;
                // Counted on the real state: a disguised spy takes no slot.
                const cost = unitCost(realState, rules, playerId, unitType);
                // Labourers take workers' places: count what shares the cap.
                const count = capCount(realState, rules, playerId, unitType);
                const cap = unitCap(realState, rules, playerId, unitType);
                const check = canSpawn(realState, { terrain, rules }, playerId, city.id, unitType);
                const era = unitCityLevel(rules, unitType);
                // More than the cap (a city fell): say why, not a bare "44/26",
                // whatever canSpawn refused first; any other reason joins it
                // in the title.
                const overCap = overCapText(count, cap);
                const capped = check.ok ? null : overCap;
                const other = check.ok
                  ? null
                  : era > cityLevel
                    ? `Needs ${cityAtLevelText(rules, cityLabel, era)}`
                    : check.reason === 'cap' && capped
                      ? null
                      : spawnRejectionText(
                          realState,
                          rules,
                          playerId,
                          unitType,
                          check.reason,
                          city.id,
                          cityLabel
                        );
                const reason = check.ok
                  ? null
                  : [capped?.title, other].filter(Boolean).join('. ') || null;
                // The card's one line fits ~12 characters: a lock names the
                // city first ("City 2: Barracks"); the title has it in full.
                const req = unitDef.requires;
                const short = check.ok
                  ? null
                  : era > cityLevel
                    ? `${cityLabel}: L${era}`
                    : check.reason === 'locked' && req
                      ? `${cityLabel}: ${rules.buildings[req.building]?.name ?? req.building}${req.level > 1 ? ` ${req.level}` : ''}`
                      : null;
                // Trainable, but every store that takes it is full: it waits outside.
                const note =
                  check.ok && unitDef.military
                    ? barracksFullText(realState, rules, city.id, unitType)
                    : null;
                // Timed units: how long each takes, and how many are queued here.
                const time = trainTimeText(rules, unitType);
                const inQueue = queued.get(unitType) ?? 0;
                return (
                  <button
                    key={unitType}
                    type="button"
                    disabled={!check.ok}
                    title={`${unitDef.name} (${costLabel(cost)}${time ? `, trains in ${time}` : ''})${inQueue > 0 ? `. ${inQueue} queued here` : ''}${reason ? `: ${reason}` : note ? `: ${note}, it will wait outside` : ''}`}
                    onClick={() => dispatch({ type: 'spawn', cityId: city.id, unitType })}
                    aria-describedby={hover?.type === unitType ? 'unit-preview' : undefined}
                    onPointerEnter={(e) => showPreview(e.currentTarget, unitType)}
                    onPointerLeave={() => setHover(null)}
                    onFocus={(e) => showPreview(e.currentTarget, unitType)}
                    onBlur={() => setHover(null)}
                    className="card min-w-0 px-1 py-[3px] leading-tight"
                  >
                    <span className="flex w-full items-baseline gap-1">
                      <span className="min-w-0 truncate text-[11px] font-medium">
                        {unitDef.name}
                      </span>
                      <span className="flex-1" />
                      {inQueue > 0 && (
                        <span className="shrink-0 text-[10px] font-medium tabular-nums text-accent">
                          ×{inQueue}
                        </span>
                      )}
                      {time && (
                        <span className="shrink-0 text-[10px] tabular-nums text-fg-subtle">
                          {time}
                        </span>
                      )}
                    </span>
                    <span
                      className={`flex w-full justify-between gap-x-1 text-[10px] ${dense ? '' : 'flex-wrap'}`}
                    >
                      <span
                        className={`whitespace-nowrap text-fg-muted ${dense ? 'min-w-0 truncate' : ''}`}
                      >
                        <Cost cost={cost} stock={stockpile} />
                      </span>
                      {Number.isFinite(cap) && (
                        <span
                          title={overCap?.title}
                          className={`ml-auto tabular-nums ${count >= cap ? 'text-warn' : 'text-fg-subtle'}`}
                        >
                          {count}/{cap}
                        </span>
                      )}
                    </span>
                    <span className="h-[12.5px] w-full truncate text-[10px] text-warn">
                      {capped ? capped.short : (short ?? reason ?? note)}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}

        {/* Build: place a site in this city's territory; automated workers build it */}
        {tab === 'build' && (
          // Three columns keep all eight buildings, Shield Generator and
          // Watchtower included, visible without scrolling.
          <div className="grid grid-cols-3 gap-1.5">
            {placeableTypes.map((type) => {
              const cost = rules.buildings[type]?.cost ?? {};
              const max = rules.buildings[type]?.maxPerCity;
              const full = max !== undefined && builtHere(type) >= max;
              const affordable = canAfford(stockpile, cost);
              const era = buildingCityLevel(rules, type);
              const locked = era > cityLevel;
              const warn = locked || full || !affordable;
              return (
                <button
                  key={type}
                  type="button"
                  disabled={warn}
                  title={`${rules.buildings[type]?.name ?? type}: ${costLabel(cost)}${max !== undefined ? `. ${max} per city (${cityLabel} has ${builtHere(type)}, sites included)` : ''}${locked ? `. Needs ${cityAtLevelText(rules, cityLabel, era)}: each city unlocks its own buildings` : ''}`}
                  onClick={() => onPlace(type, city.id)}
                  className="card min-w-0 px-2 py-1 leading-tight"
                >
                  {/* Name and cost both truncate in the narrow columns; the title has them in full. */}
                  <span className="flex w-full justify-between gap-2">
                    <span className="truncate font-medium">
                      {rules.buildings[type]?.name ?? type}
                    </span>
                    <span className="truncate text-[10px] text-fg-muted">
                      <Cost cost={cost} stock={stockpile} />
                    </span>
                  </span>
                  <span
                    className={`line-clamp-2 text-[10px] ${warn ? 'text-warn' : 'text-fg-subtle'}`}
                  >
                    {/* A limited building says how many the city has before placing. */}
                    {max !== undefined && !locked && !full && (
                      <span className="tabular-nums">
                        {builtHere(type)}/{max} here ·{' '}
                      </span>
                    )}
                    {locked
                      ? `Needs ${cityAtLevelText(rules, cityLabel, era)}`
                      : full
                        ? `${cityLabel} already has its own (${max} per city)`
                        : !affordable
                          ? missingLabel(stockpile, cost)
                          : placementHint(rules, type)}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Upgrades: this city's level, the buildings' levels in its territory, units that can upgrade here */}
        {tab === 'upgrades' && (
          <div className="flex flex-col gap-1">
            <UpgradeGroup
              title="This city"
              rows={upgrades.city ? [upgrades.city] : []}
              {...group}
            />
            {!upgrades.city && def?.levels && <p className="px-1 text-fg-subtle">Max level</p>}
            <UpgradeGroup title="Buildings in its territory" rows={upgrades.buildings} {...group} />
            <UpgradeGroup title="Units that can upgrade here" rows={upgrades.units} {...group} />
          </div>
        )}

        {/* Workers: target counts per task, slot pips per building, leftovers */}
        {tab === 'workers' && workers && (
          <CityWorkersTab
            city={city}
            data={workers}
            focus={focusControl}
            dispatch={dispatch}
            onSelectUnits={onSelectUnits}
            tick={state.tick}
            selectedUnitIds={selectedUnitIds}
          />
        )}
      </div>
    </DockPanel>
  );
}

/** A titled group of the Upgrades tab's folding rows; nothing when empty. */
function UpgradeGroup({
  title,
  rows,
  open,
  stock,
  onToggle,
  onHighlight,
}: {
  title: string;
  rows: readonly UpgradeEntry[];
  open: string;
  stock: Record<string, number>;
  onToggle: (id: string) => void;
  onHighlight?: (ids: readonly string[]) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <section className="flex flex-col gap-0.5">
      <p className="flex justify-between px-2 pt-1 text-[10px] uppercase tracking-wider text-fg-subtle">
        <span>{title}</span>
        <span className="tabular-nums">{rows.length}</span>
      </p>
      {rows.map((e) => (
        <UpgradeFold
          key={e.id}
          entry={e}
          open={open === e.id}
          stock={stock}
          onToggle={() => onToggle(e.id)}
          onHighlight={onHighlight}
        />
      ))}
    </section>
  );
}
